import { inject, Injectable } from '@angular/core';
import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  deleteField,
  doc,
  docSnapshots,
  DocumentData,
  DocumentSnapshot,
  Firestore,
  getCountFromServer,
  getDoc,
  getDocs,
  query,
  QueryDocumentSnapshot,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from '@angular/fire/firestore';
import {
  GoalMateProgram,
  GoalMateProgramDay,
  GoalMateProgramDayCreateDto,
  GoalMateProgramDayPrivate,
  GoalMateProgramDto,
  GoalMateProgramGroup,
  GoalMateProgramGroupDto,
  UpdatePrpogramGroups,
} from '@goalmate/typings';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class GoalmateProgramRepositoryService {
  private firestore = inject(Firestore);

  /**
   * Collection string for the programs
   */
  protected collectionProgramsStr = 'goalmate-programs';
  /**
   * Collection string for the program groups
   */
  protected collectionGroupStr = 'goalmate-program-groups';
  /**
   * Collection string for the program days
   */
  protected collectionDaysStr = 'goalmate-program-days';

  async addNewProgram(program: GoalMateProgramDto): Promise<GoalMateProgram> {
    const programRef = await addDoc(
      collection(this.firestore, this.collectionProgramsStr),
      program,
    );
    return { ...program, id: programRef.id };
  }

  async getProgramById(programId: string): Promise<GoalMateProgram> {
    const programRef = doc(
      this.firestore,
      `${this.collectionProgramsStr}/${programId}`,
    );
    const programSnap = await getDoc(programRef);
    return this.convertSnapshot(programSnap);
  }

  async updateProgram(
    programId: string,
    program: Partial<GoalMateProgramDto>,
  ): Promise<void> {
    if (program.videoId === '') program.videoId = deleteField() as any;
    if (program.videoThumbnail === '')
      program.videoThumbnail = deleteField() as any;
    const programRef = doc(
      this.firestore,
      `${this.collectionProgramsStr}/${programId}`,
    );
    await setDoc(programRef, program, { merge: true });
  }

  async createGroup(
    groupDto: GoalMateProgramGroupDto,
  ): Promise<GoalMateProgramGroup> {
    const contentRef = await addDoc(
      collection(this.firestore, this.collectionGroupStr),
      groupDto,
    );
    const { contentIds, ...group } = groupDto;
    return { ...group, id: contentRef.id, contents: [] };
  }

  async deleteGroup(groupId: string): Promise<void> {
    await deleteDoc(
      doc(this.firestore, `${this.collectionGroupStr}/${groupId}`),
    );
  }

  async getAllGroups(programId: string): Promise<GoalMateProgramGroup[]> {
    const groupsCollectionRef = collection(
      this.firestore,
      this.collectionGroupStr,
    );
    const q = query(groupsCollectionRef, where('programId', '==', programId));
    const groupsRef = await getDocs(q);
    const groups = groupsRef.docs.map(this.convertSnapshot.bind(this));
    for (const group of groups) {
      const contentIds = group.contentIds;
      if (contentIds.length === 0) {
        group.contents = [];
        delete group.contentIds;
        continue;
      }
      const programRef = collection(this.firestore, this.collectionDaysStr);
      const q = query(programRef, where('__name__', 'in', contentIds));
      const querySnapshot = await getDocs(q);
      const contents = querySnapshot.docs.map(
        this.convertSnapshotWithDates.bind(this),
      );
      group.contents = group.contentIds.map((id: string) =>
        contents.find((content) => content.id === id),
      );
      delete group.contentIds;
    }
    return groups;
  }

  async updateProgramGroup(
    groupId: string,
    group: Partial<GoalMateProgramGroupDto>,
  ): Promise<void> {
    const groupRef = doc(
      this.firestore,
      `${this.collectionGroupStr}/${groupId}`,
    );
    await setDoc(groupRef, group, { merge: true });
  }

  async updateProgramGroups(data: UpdatePrpogramGroups): Promise<void> {
    const batch = writeBatch(this.firestore);
    data.forEach((content) => {
      const contentRef = doc(
        this.firestore,
        `${this.collectionGroupStr}/${content.id}`,
      );
      batch.set(contentRef, content.changes, { merge: true });
    });
    await batch.commit();
  }

  async getGroupsCount(programId: string) {
    const programRef = collection(this.firestore, this.collectionGroupStr);
    const q = query(programRef, where('programId', '==', programId));
    const querySnapshot = await getCountFromServer(q);
    return querySnapshot.data().count;
  }

  async getDayPrivateData(dayId: string) {
    const privateDatRef = doc(
      this.firestore,
      `${this.collectionDaysStr}/${dayId}/private/data`,
    );
    const privateContentSnap = await getDoc(privateDatRef);
    if (!privateContentSnap.exists()) return null;
    return privateContentSnap.data();
  }

  getDayPrivateData$(
    dayId: string,
  ): Observable<Partial<GoalMateProgramDayPrivate>> {
    const privateDataRef = doc(
      this.firestore,
      `${this.collectionDaysStr}/${dayId}/private/data`,
    );
    return docSnapshots(privateDataRef).pipe(
      map((snap) => snap.data() as Partial<GoalMateProgramDayPrivate>),
    );
  }

  async createDay(
    data: GoalMateProgramDayCreateDto,
    groupId: string,
  ): Promise<GoalMateProgramDay> {
    data.createdAt = Timestamp.now();
    data.updatedAt = Timestamp.now();
    const { message, ...publicDay } = data;
    // Create public day
    const publicDayRef = await addDoc(
      collection(this.firestore, this.collectionDaysStr),
      publicDay,
    );
    // Create private day
    const privateColRef = collection(publicDayRef, '/private');
    const privateDocRef = doc(privateColRef, 'data');
    await setDoc(privateDocRef, { message });
    // Add day Id to group
    const groupRef = doc(
      this.firestore,
      `${this.collectionGroupStr}/${groupId}`,
    );
    await updateDoc(groupRef, { contentIds: arrayUnion(publicDayRef.id) });
    // Return the new day
    const newProgramDay: GoalMateProgramDay = {
      ...data,
      id: publicDayRef.id,
      createdAt: data.createdAt.toDate(),
      updatedAt: data.updatedAt.toDate(),
    };
    return newProgramDay;
  }

  async getAllPrograms(): Promise<GoalMateProgram[]> {
    const programsRef = await getDocs(
      collection(this.firestore, this.collectionProgramsStr),
    );
    const programs = programsRef.docs.map(this.convertSnapshot.bind(this));
    return programs;
  }

  async getActivePrograms(): Promise<GoalMateProgram[]> {
    const collectionRef = collection(
      this.firestore,
      this.collectionProgramsStr,
    );

    const q = query(collectionRef, where('isActive', '==', true));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(this.convertSnapshot.bind(this));
  }

  async updateDayPublicData(
    dayId: string,
    data: Partial<GoalMateProgramDayCreateDto>,
  ): Promise<void> {
    const dayRef = doc(this.firestore, `${this.collectionDaysStr}/${dayId}`);
    data.updatedAt = Timestamp.now();
    await setDoc(dayRef, data, { merge: true });
  }

  async updateDayPrivateData(
    dayId: string,
    data: Partial<GoalMateProgramDayPrivate>,
  ): Promise<void> {
    Object.keys(data).forEach((key) => {
      if (!(data as any)[key]) {
        (data as any)[key] = deleteField();
      }
    });
    const privateDataRef = doc(
      this.firestore,
      `${this.collectionDaysStr}/${dayId}/private/data`,
    );
    await setDoc(privateDataRef, data, { merge: true });
  }

  async deleteContentDay(dayId: string, groupId: string): Promise<void> {
    // Day ref
    const dayRef = doc(this.firestore, `${this.collectionDaysStr}/${dayId}`);
    // Group ref
    const groupRef = doc(
      this.firestore,
      `${this.collectionGroupStr}/${groupId}`,
    );
    // Private data ref
    const privateDataRef = doc(dayRef, '/private/data');
    // Delete day with private data and remove day from group
    await Promise.all([
      updateDoc(groupRef, { contentIds: arrayRemove(dayId) }),
      deleteDoc(privateDataRef),
      deleteDoc(dayRef),
    ]);
  }

  private convertSnapshot(
    snap: QueryDocumentSnapshot<DocumentData> | DocumentSnapshot<DocumentData>,
  ) {
    const data = snap.data();
    return {
      ...data,
      id: snap.id,
    } as any;
  }

  private convertSnapshotWithDates(
    snap: QueryDocumentSnapshot<DocumentData> | DocumentSnapshot<DocumentData>,
  ) {
    const data = this.convertSnapshot(snap);
    return {
      ...data,
      createdAt: data?.['createdAt']?.toDate(),
      updatedAt: data?.['updatedAt']?.toDate(),
    } as any;
  }
}
